(() => {
  const money = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value) || 0);

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[character]);

  const initPortal = (root) => {
    if (!root || root.dataset.wholesaleV2Ready === 'true') return;
    root.dataset.wholesaleV2Ready = 'true';

    const apiBase = String(root.dataset.apiBase || 'https://calculator.viallock.com').replace(/\/$/, '');
    const loginPanel = root.querySelector('[data-wholesale-login-panel]');
    const loginForm = root.querySelector('[data-wholesale-login]');
    const emailInput = root.querySelector('[data-wholesale-email-login]');
    const passwordInput = root.querySelector('[data-wholesale-password]');
    const passwordPanel = root.querySelector('[data-wholesale-password-panel]');
    const passwordForm = root.querySelector('[data-wholesale-password-form]');
    const currentPasswordInput = root.querySelector('[data-wholesale-current-password]');
    const newPasswordInput = root.querySelector('[data-wholesale-new-password]');
    const confirmPasswordInput = root.querySelector('[data-wholesale-confirm-password]');
    const forgotButton = root.querySelector('[data-wholesale-forgot]');
    const applicationForm = root.querySelector('#VialLockWholesaleApplication');
    const applicationStatus = root.querySelector('[data-wholesale-application-status]');
    const portal = root.querySelector('[data-wholesale-portal]');
    const accountBar = root.querySelector('[data-wholesale-account]');
    const clinicName = root.querySelector('[data-wholesale-clinic]');
    const pricingTier = root.querySelector('[data-wholesale-tier]');
    const searchInput = root.querySelector('[data-wholesale-search]');
    const catalogElement = root.querySelector('[data-wholesale-catalog]');
    const countElement = root.querySelector('[data-wholesale-count]');
    const message = root.querySelector('[data-wholesale-message]');
    const orderForm = root.querySelector('[data-wholesale-order]');
    const orderSummary = root.querySelector('[data-wholesale-summary]');
    const orderTotal = root.querySelector('[data-wholesale-total]');
    const orderButton = root.querySelector('[data-wholesale-submit]');
    const customerName = root.querySelector('[data-wholesale-customer-name]');
    const orderNotes = root.querySelector('[data-wholesale-notes]');
    const brandingToggle = root.querySelector('[data-wholesale-branding-toggle]');
    const brandingFields = root.querySelector('[data-wholesale-branding-fields]');
    const brandingNote = root.querySelector('[data-wholesale-branding-note]');
    const brandName = root.querySelector('[data-wholesale-brand-name]');
    const logoLink = root.querySelector('[data-wholesale-logo-link]');
    const logoPlacement = root.querySelector('[data-wholesale-logo-placement]');
    const logoNotes = root.querySelector('[data-wholesale-logo-notes]');
    const logoutButton = root.querySelector('[data-wholesale-logout]');

    let catalog = [];
    let account = null;
    let orderItems = [];
    let pendingEmail = '';

    const token = () => sessionStorage.getItem('vl_wholesale_token') || '';
    const setToken = (value) => {
      localStorage.removeItem('wh_token');
      if (value) sessionStorage.setItem('vl_wholesale_token', value);
      else sessionStorage.removeItem('vl_wholesale_token');
    };

    const showMessage = (text, tone = 'info') => {
      if (!message) return;
      message.hidden = !text;
      message.dataset.tone = tone;
      message.textContent = text || '';
    };

    const showApplicationStatus = (text, tone = 'success') => {
      if (!applicationStatus) return;
      applicationStatus.hidden = !text;
      applicationStatus.dataset.tone = tone;
      applicationStatus.textContent = text || '';
    };

    const api = async (path, options = {}) => {
      const response = await fetch(`${apiBase}${path}`, {
        ...options,
        headers: {
          Accept: 'application/json',
          ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
          ...(options.headers || {}),
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(payload.error || 'The wholesale service is temporarily unavailable.');
        error.status = response.status;
        error.payload = payload;
        throw error;
      }
      return payload;
    };

    const syncBrandingFields = () => {
      const enabled = Boolean(brandingToggle?.checked);
      if (brandingFields) brandingFields.hidden = !enabled;
      if (brandingNote) brandingNote.hidden = !enabled;
    };

    const showPasswordSetup = (enabled) => {
      if (loginPanel) loginPanel.hidden = enabled;
      if (passwordPanel) passwordPanel.hidden = !enabled;
      if (portal) portal.hidden = true;
      root.classList.remove('vl-wholesale--portal-active');
      if (enabled) {
        currentPasswordInput?.focus();
        showMessage('For security, replace the temporary password before opening wholesale pricing.', 'info');
      }
    };

    const clearPortal = () => {
      setToken('');
      catalog = [];
      account = null;
      orderItems = [];
      pendingEmail = '';
      if (portal) portal.hidden = true;
      if (accountBar) accountBar.hidden = true;
      if (orderForm) orderForm.hidden = true;
      if (catalogElement) catalogElement.innerHTML = '';
      if (countElement) countElement.textContent = '0 products';
      if (passwordPanel) passwordPanel.hidden = true;
      if (loginPanel) loginPanel.hidden = false;
      root.classList.remove('vl-wholesale--portal-active');
      updateOrderSummary();
    };

    const setPortalData = (payload) => {
      account = payload.account || account || {};
      catalog = Array.isArray(payload.catalog) ? payload.catalog : [];
      if (clinicName) clinicName.textContent = account.clinicName || account.businessName || account.email || 'Wholesale account';
      if (pricingTier) pricingTier.textContent = account.pricingTier || 'Wholesale pricing';
      if (accountBar) accountBar.hidden = false;
      if (orderForm) orderForm.hidden = false;
      if (loginPanel) loginPanel.hidden = true;
      if (passwordPanel) passwordPanel.hidden = true;
      if (portal) portal.hidden = false;
      root.classList.add('vl-wholesale--portal-active');
      renderCatalog();
      updateOrderSummary();
      showMessage('');
      window.history.replaceState({}, '', '/pages/wholesale?view=wholesale-portal');
    };

    const variantById = (variantId) => {
      for (const product of catalog) {
        const variant = (product.variants || []).find((entry) => String(entry.id) === String(variantId));
        if (variant) return { product, variant };
      }
      return null;
    };

    const updateOrderSummary = () => {
      const total = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      if (orderTotal) orderTotal.textContent = money(total);
      if (orderButton) orderButton.disabled = orderItems.length === 0;
      if (!orderSummary) return;

      orderSummary.innerHTML = orderItems.length
        ? orderItems.map((item) => `
          <div class="vl-wholesale__order-line">
            <span>${item.quantity} × ${escapeHtml(item.productTitle)} / ${escapeHtml(item.variantTitle)}</span>
            <div>
              <strong>${money(item.price * item.quantity)}</strong>
              <button type="button" data-wholesale-remove="${escapeHtml(item.variantId)}" aria-label="Remove ${escapeHtml(item.productTitle)}">Remove</button>
            </div>
          </div>
        `).join('')
        : '<p class="vl-wholesale__empty">Add products from the catalog to build a wholesale invoice.</p>';
    };

    const renderCatalog = () => {
      if (!catalogElement || !countElement) return;
      const query = String(searchInput?.value || '').trim().toLowerCase();
      const visible = catalog.filter((product) => {
        const haystack = [
          product.title,
          product.handle,
          ...(product.variants || []).flatMap((variant) => [variant.title, variant.sku, variant.color]),
        ].join(' ').toLowerCase();
        return haystack.includes(query);
      });

      countElement.textContent = `${visible.length} product${visible.length === 1 ? '' : 's'}`;
      catalogElement.innerHTML = visible.map((product) => {
        const availableVariants = (product.variants || []).filter((variant) => variant.available !== false);
        const options = availableVariants.map((variant) => `
          <option value="${escapeHtml(variant.id)}" data-price="${escapeHtml(variant.price)}">
            ${escapeHtml(variant.title)} · ${money(variant.price)}
          </option>
        `).join('');
        const first = availableVariants[0];
        const range = product.minimumPrice === product.maximumPrice
          ? money(product.minimumPrice)
          : `${money(product.minimumPrice)}–${money(product.maximumPrice)}`;

        return `
          <article class="vl-wholesale__card" data-wholesale-card data-product-id="${escapeHtml(product.id)}">
            <div class="vl-wholesale__media">
              ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : '<span>VialLock</span>'}
            </div>
            <div class="vl-wholesale__body">
              <div>
                <h2>${escapeHtml(product.title)}</h2>
                <p>${escapeHtml(first?.sku || '')}</p>
              </div>
              <div class="vl-wholesale__variant">
                <div class="vl-wholesale__price-row">
                  <strong data-wholesale-card-price>${range}</strong>
                  <span>Approved account pricing</span>
                </div>
                ${availableVariants.length ? `
                  <div class="vl-wholesale__variant-buy">
                    <label>
                      Product option
                      <select data-wholesale-variant>${options}</select>
                    </label>
                    <label>
                      Qty
                      <input type="number" min="1" step="1" value="1" data-wholesale-quantity>
                    </label>
                    <button type="button" data-wholesale-add>Add to order</button>
                  </div>
                ` : '<p>This product is currently unavailable.</p>'}
              </div>
            </div>
          </article>
        `;
      }).join('');
    };

    const buildOrderNote = () => {
      const parts = [];
      if (customerName?.value.trim()) parts.push(`Order contact: ${customerName.value.trim()}`);
      if (orderNotes?.value.trim()) parts.push(`Order notes: ${orderNotes.value.trim()}`);
      if (brandingToggle?.checked) {
        const branding = [
          'Custom-logo quote requested: Yes',
          brandName?.value.trim() ? `Brand or clinic name: ${brandName.value.trim()}` : '',
          logoLink?.value.trim() ? `Artwork link: ${logoLink.value.trim()}` : '',
          logoPlacement?.value.trim() ? `Placement / color notes: ${logoPlacement.value.trim()}` : '',
          logoNotes?.value.trim() ? `Branded-order notes: ${logoNotes.value.trim()}` : '',
        ].filter(Boolean).join('\n');
        parts.push(branding);
      }
      return parts.join('\n\n');
    };

    loginForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      pendingEmail = emailInput.value.trim();
      showMessage('Opening your wholesale account...');
      try {
        const payload = await fetch(`${apiBase}/wholesale-auth/login`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: pendingEmail, password: passwordInput.value }),
        }).then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || 'Invalid email or password.');
          return data;
        });
        setToken(payload.token);
        if (payload.mustChangePassword) showPasswordSetup(true);
        else setPortalData(payload);
      } catch (error) {
        showMessage(error.message, 'error');
      }
    });

    passwordForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (newPasswordInput.value !== confirmPasswordInput.value) {
        showMessage('The new passwords do not match.', 'error');
        return;
      }
      showMessage('Securing your wholesale account...');
      try {
        const payload = await api('/wholesale-auth/change-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            currentPassword: currentPasswordInput.value,
            newPassword: newPasswordInput.value,
          }),
        });
        setToken(payload.token);
        passwordForm.reset();
        setPortalData(payload);
      } catch (error) {
        showMessage(error.message, 'error');
      }
    });

    forgotButton?.addEventListener('click', async () => {
      const email = emailInput?.value.trim();
      if (!email) {
        showMessage('Enter your approved wholesale email first.', 'error');
        emailInput?.focus();
        return;
      }
      forgotButton.disabled = true;
      try {
        const response = await fetch(`${apiBase}/wholesale-auth/reset-password`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(result.error || 'Password reset email could not be sent.');
        }
        showMessage('If that email is approved, a temporary password is on its way.', 'success');
      } catch (error) {
        console.error('Wholesale password reset failed:', error);
        showMessage(
          'We could not send the reset email. Your existing password is unchanged. Please contact wholesale@viallock.com.',
          'error'
        );
      } finally {
        forgotButton.disabled = false;
      }
    });

    applicationForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      showApplicationStatus('Submitting your application...', 'info');
      const values = new FormData(applicationForm);
      const payload = {
        businessName: values.get('contact[Business name]'),
        contactName: values.get('contact[name]'),
        email: values.get('contact[email]'),
        phone: values.get('contact[phone]'),
        businessType: values.get('contact[Business type]'),
        website: values.get('contact[Website]'),
        expectedMonthlyVolume: values.get('contact[Expected monthly volume]'),
        vialSizeInterest: values.get('contact[Vial size interest]'),
        orderTimeline: values.get('contact[Order timeline]'),
        shippingZip: values.get('contact[Shipping ZIP]'),
        logoInterest: values.get('contact[Custom logo interest]'),
        notes: values.get('contact[body]'),
      };
      const submit = applicationForm.querySelector('button[type="submit"]');
      if (submit) submit.disabled = true;
      try {
        const response = await fetch(`${apiBase}/wholesale-auth/apply`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Unable to submit the application.');
        applicationForm.reset();
        showApplicationStatus(data.message || 'Application received. We will email you after review.', 'success');
      } catch (error) {
        showApplicationStatus(error.message, 'error');
      } finally {
        if (submit) submit.disabled = false;
      }
    });

    searchInput?.addEventListener('input', renderCatalog);

    catalogElement?.addEventListener('change', (event) => {
      const select = event.target.closest('[data-wholesale-variant]');
      if (!select) return;
      const card = select.closest('[data-wholesale-card]');
      const selected = variantById(select.value);
      const price = card?.querySelector('[data-wholesale-card-price]');
      if (price && selected) price.textContent = money(selected.variant.price);
    });

    catalogElement?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wholesale-add]');
      if (!button) return;
      const card = button.closest('[data-wholesale-card]');
      const select = card?.querySelector('[data-wholesale-variant]');
      const quantityInput = card?.querySelector('[data-wholesale-quantity]');
      const selected = variantById(select?.value);
      const quantity = Math.max(1, Math.floor(Number(quantityInput?.value) || 1));
      if (!selected) return;

      const existing = orderItems.find((item) => item.variantId === selected.variant.id);
      if (existing) existing.quantity += quantity;
      else {
        orderItems.push({
          variantId: selected.variant.id,
          productTitle: selected.product.title,
          variantTitle: selected.variant.title,
          price: Number(selected.variant.price) || 0,
          quantity,
        });
      }
      if (quantityInput) quantityInput.value = 1;
      updateOrderSummary();
      showMessage(`${selected.product.title} / ${selected.variant.title} added to the wholesale order.`, 'success');
    });

    orderSummary?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wholesale-remove]');
      if (!button) return;
      orderItems = orderItems.filter((item) => item.variantId !== button.dataset.wholesaleRemove);
      updateOrderSummary();
    });

    brandingToggle?.addEventListener('change', syncBrandingFields);
    syncBrandingFields();

    orderForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!orderItems.length) return;
      orderButton.disabled = true;
      showMessage('Creating your secure Shopify wholesale invoice...');
      try {
        const payload = await api('/wholesale/draft-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: orderItems.map((item) => ({
              variantId: item.variantId,
              quantity: item.quantity,
            })),
            note: buildOrderNote(),
          }),
        });
        orderItems = [];
        orderForm.reset();
        syncBrandingFields();
        updateOrderSummary();
        showMessage('Wholesale invoice created. Opening secure Shopify checkout...', 'success');
        if (payload.invoiceUrl) window.location.href = payload.invoiceUrl;
      } catch (error) {
        showMessage(error.message, 'error');
        updateOrderSummary();
      }
    });

    logoutButton?.addEventListener('click', () => {
      clearPortal();
      showMessage('You have been logged out.', 'success');
      window.history.replaceState({}, '', '/pages/wholesale');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    if (token()) {
      showMessage('Restoring your wholesale account...');
      api('/wholesale/me')
        .then(setPortalData)
        .catch((error) => {
          if (error.status === 428 || error.payload?.mustChangePassword) showPasswordSetup(true);
          else {
            clearPortal();
            showMessage('');
          }
        });
    }
  };

  const boot = () => document.querySelectorAll('[data-vl-wholesale]').forEach(initPortal);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
